import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Key, PaperPlaneTilt, Ticket, Rocket, Copy, CheckCircle, Clock } from "@phosphor-icons/react";

export default function Plan() {
  const { user, refresh } = useAuth();
  const [keyInput, setKeyInput] = useState("");
  const [keys, setKeys] = useState([]);
  const [sendEmail, setSendEmail] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/my/keys").then((r) => setKeys(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const activate = async (key) => {
    setBusy(true);
    try {
      await api.post("/activate", { key });
      toast.success("Key activated — locked to your email");
      setKeyInput("");
      refresh && (await refresh());
      load();
    } catch (err) {
      toast.error(apiError(err), { duration: 12000 });
    } finally {
      setBusy(false);
    }
  };

  const sendKey = async (key) => {
    const email = (sendEmail[key] || "").trim();
    if (!email) return;
    try {
      await api.post(`/keys/${key}/send`, { email });
      toast.success(`Marked as sent to ${email}. Share the key with them to activate.`);
      setSendEmail({ ...sendEmail, [key]: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const copy = (k) => {
    navigator.clipboard.writeText(k);
    toast.success("Key copied");
  };

  const spareKeys = keys.filter((k) => !k.is_active_for_me);

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 pt-28 pb-20">
        <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-2">// Plan & Keys</p>
        <h1 className="font-mono-p font-bold uppercase tracking-tight text-3xl border-b border-zinc-800 pb-8">
          Your Plan
        </h1>

        {/* Current plan */}
        <div className="mt-8 border border-zinc-800 bg-[#090A0C] p-6" data-testid="current-plan">
          {user?.plan_name ? (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-mono-p text-xs uppercase tracking-widest text-cyan-400">Active Plan</p>
                <h2 className="font-mono-p font-bold text-2xl mt-1">{user.plan_name}</h2>
                <p className="text-sm text-zinc-500 mt-1">
                  {user.cards_limit} AI ID Card(s) · {user.ai_max} AI max · locked to {user.email}
                </p>
              </div>
              <span className="font-mono-p text-xs tracking-wider text-cyan-400 border border-cyan-400/30 px-3 py-2">
                {user.active_key}
              </span>
            </div>
          ) : (
            <div className="text-center py-6">
              <Ticket size={36} weight="duotone" className="text-zinc-700 mx-auto mb-3" />
              <p className="text-sm text-zinc-400">No active plan. Purchase from the Store, or activate a key below.</p>
            </div>
          )}
          {user?.warroom && (
            <div className="mt-4 flex items-center gap-2 text-sm text-amber-400 border-t border-zinc-800 pt-4">
              <Rocket size={16} /> S.I.M.O.N. subscription active
            </div>
          )}
        </div>

        {/* Activate a key */}
        {!user?.plan_name && (
          <div className="mt-6 border border-zinc-800 bg-[#090A0C] p-6">
            <h3 className="font-mono-p text-xs uppercase tracking-widest text-cyan-400 mb-4 flex items-center gap-2">
              <Key size={14} /> Activate a Key
            </h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                data-testid="activate-key-input"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value.toUpperCase())}
                placeholder="PRID-XXXX-XXXX-XXXX"
                className="flex-1 bg-black border border-zinc-800 px-4 py-3 text-cyan-300 font-mono-p text-sm tracking-wider focus:border-cyan-400/60 focus:outline-none transition-colors"
              />
              <button
                data-testid="activate-key-btn"
                onClick={() => activate(keyInput)}
                disabled={busy || !keyInput}
                className="font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black px-6 py-3 font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50"
              >
                Activate
              </button>
            </div>
            <p className="mt-3 text-xs text-zinc-600">
              A key locks permanently to the first email that activates it and can never be reused.
            </p>
          </div>
        )}

        {/* Spare keys to send out */}
        {spareKeys.length > 0 && (
          <div className="mt-6 border border-zinc-800 bg-[#090A0C] p-6" data-testid="spare-keys">
            <h3 className="font-mono-p text-xs uppercase tracking-widest text-cyan-400 mb-1 flex items-center gap-2">
              <PaperPlaneTilt size={14} /> Your Spare Keys
            </h3>
            <p className="text-xs text-zinc-600 mb-4">
              Send each spare key to a teammate. They register their own email and activate it in-app — it then locks to them.
            </p>
            <div className="space-y-4">
              {spareKeys.map((k) => (
                <div key={k.key} className="border border-zinc-800 bg-black/40 p-4" data-testid={`spare-${k.key}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <button
                      onClick={() => copy(k.key)}
                      className="flex items-center gap-2 font-mono-p text-sm text-cyan-300 hover:text-cyan-200"
                      data-testid={`copy-${k.key}`}
                    >
                      {k.key} <Copy size={13} className="text-zinc-600" />
                    </button>
                    {k.activated_email ? (
                      <span className="flex items-center gap-1.5 text-xs text-green-400 font-mono-p uppercase">
                        <CheckCircle size={14} /> Activated · {k.activated_email}
                      </span>
                    ) : k.sent_to ? (
                      <span className="flex items-center gap-1.5 text-xs text-amber-400 font-mono-p uppercase">
                        <Clock size={14} /> Sent to {k.sent_to} · awaiting activation
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-600 font-mono-p uppercase">Unused</span>
                    )}
                  </div>
                  {!k.activated_email && (
                    <div className="mt-3 flex flex-col sm:flex-row gap-3 border-t border-zinc-800 pt-3">
                      <input
                        data-testid={`send-input-${k.key}`}
                        type="email"
                        value={sendEmail[k.key] || ""}
                        onChange={(e) => setSendEmail({ ...sendEmail, [k.key]: e.target.value })}
                        placeholder="teammate@email.com"
                        className="flex-1 bg-black border border-zinc-800 px-4 py-2.5 text-zinc-100 text-sm focus:border-cyan-400/60 focus:outline-none transition-colors"
                      />
                      <button
                        data-testid={`send-btn-${k.key}`}
                        onClick={() => sendKey(k.key)}
                        className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest border border-zinc-700 px-4 py-2.5 text-zinc-200 hover:border-cyan-400/50 hover:text-cyan-400 transition-colors"
                      >
                        <PaperPlaneTilt size={14} /> Mark Sent
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
